
import { ThemeProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { theme } from './theme/theme'
import { AuthProvider } from './contexts/AuthContext'
import { ThemeContextProvider } from './contexts/ThemeContext'
import { AuthGuard } from './components/auth/AuthGuard'

function ConditionalLayout({ children }: { children: React.ReactNode }) {
  const location = useLocation()
  
  // Don't wrap auth pages in dashboard layout
  const isAuthPage = location.pathname === '/login' || location.pathname === '/forgot-password' || location.pathname === '/register'
  
  if (isAuthPage) {
    return <>{children}</>
  }
  
  return (
    <AuthGuard>
      {children}
    </AuthGuard>
  )
}

export function Providers({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(() => new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
        retry: (failureCount, error: any) => {
          // Only retry on network errors, not on 404s or other client errors
          if (error?.status >= 400 && error?.status < 500) {
            return false
          }
          return failureCount < 3
        },
        refetchOnWindowFocus: false, // Better for SPA experience
      },
    },
  }))

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ThemeContextProvider>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <ConditionalLayout>
              {children}
            </ConditionalLayout>
          </ThemeProvider>
        </ThemeContextProvider>
      </AuthProvider>
    </QueryClientProvider>
  )
}